import { useState, useEffect, createContext, useContext } from 'react'
import { authApi } from '../../lib/api'
import { User } from '../../types'

interface AuthContextType {
  user: User | null
  token: string | null
  isAuthenticated: boolean
  loading: boolean
  login: (email: string, password: string) => Promise<void>
  register: (email: string, password: string, displayName?: string, region?: string) => Promise<void>
  logout: () => void
  refreshUser: () => Promise<void>
}

const AuthContext = createContext<AuthContextType | null>(null)

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<User | null>(null)
  const [token, setToken] = useState<string | null>(localStorage.getItem('token'))
  const [loading, setLoading] = useState(true)

  const isAuthenticated = !!token && !!user

  const refreshUser = async () => {
    try {
      const user = await authApi.me()
      setUser(user)
    } catch (err: any) {
      const errorMsg = err.message || ''
      const isTokenError = errorMsg.includes('Invalid token') ||
                          errorMsg.includes('401') ||
                          errorMsg.includes('Unauthorized') ||
                          errorMsg.includes('Token expired')

      if (isTokenError) {
        localStorage.removeItem('token')
        setToken(null)
        setUser(null)
      }
      // 不设置 user 为 null，保留当前状态
      console.error('Failed to refresh user:', errorMsg)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    if (token) {
      refreshUser().finally(() => setLoading(false))
    } else {
      setLoading(false)
    }
  }, [token])

  const login = async (email: string, password: string) => {
    const { token: newToken, user: newUser } = await authApi.login({ email, password })
    localStorage.setItem('token', newToken)
    setToken(newToken)
    setUser(newUser)
  }

  const register = async (email: string, password: string, displayName?: string, region?: string) => {
    const { token: newToken, user: newUser } = await authApi.register({ email, password, displayName, region })
    localStorage.setItem('token', newToken)
    setToken(newToken)
    setUser(newUser)
  }

  const logout = () => {
    localStorage.removeItem('token')
    setToken(null)
    setUser(null)
  }

  return (
    <AuthContext.Provider value={{ user, token, isAuthenticated, loading, login, register, logout, refreshUser }}>
      {children}
    </AuthContext.Provider>
  )
}

export function useAuth() {
  const context = useContext(AuthContext)
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}
